import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { ResearcherLevelBadge } from '../ResearcherLevelBadge';
import type { User } from '../../app/feathers/users/User';

const LEVEL_LABELS: Record<number, string> = {
  1: 'Level 1',
  2: 'Level 2',
  3: 'Level 3',
};

function getCurrentTrainingLevel(user: User): number | null {
  const publisherLevel = user.publisherLevel ?? 0;
  const researcherLevel = user.researcherLevel ?? 1;
  if (publisherLevel >= 3) return null;
  return Math.min(publisherLevel + 1, researcherLevel);
}

export interface CompleteYourTrainingProps {
  user: User;
}

/**
 * Shown on the home page for users in training. Displays current level (badge + label),
 * a horizontal progress bar (profiles done vs batch size), and pending review count.
 */
export const CompleteYourTraining: React.FC<CompleteYourTrainingProps> = ({
  user,
}) => {
  const currentLevel = getCurrentTrainingLevel(user);
  if (currentLevel == null) return null;

  const total =
    (user.trainingTotalCompletedCountAtLevel || {})[currentLevel] ?? 0;
  const allowed =
    (user.trainingAllowedCandidatesAtLevel || {})[currentLevel] ?? 0;
  const pending =
    (user.trainingPendingCountAtLevel || {})[currentLevel] ?? 0;

  const progressValue = allowed > 0 ? Math.min(100, (total / allowed) * 100) : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 600 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ResearcherLevelBadge
          researcherLevel={user.researcherLevel}
          publisherLevel={user.publisherLevel}
          permissions={user.permissions}
          level={currentLevel}
          style={{ height: 40, width: 40 }}
        />
        <Typography variant="body1" component="span">
          You are currently <b>{LEVEL_LABELS[currentLevel] ?? `Level ${currentLevel}`}</b>
        </Typography>
      </Box>
      <Box>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 10,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 1,
            },
          }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {total} of {allowed} profiles submitted · {pending} pending review
        </Typography>
      </Box>
    </Box>
  );
};

export default CompleteYourTraining;
